// src/pages/DatabasePage.js
// Read-only manager for the chatbot knowledge base. Content is ingested
// automatically by the backend sync (every 15 min from the MDESIGN APIs);
// this page shows what is in the Pinecone vector store, filtered by
// doc_domain, with sync status and manual sync trigger.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:5000') + '/database';

const DOMAIN_LABELS = {
  module: 'Module',
  software: 'Software',
  manufacturer: 'Hersteller',
  website: 'Website',
  faq: 'FAQ',
  'manufacturer news': 'Hersteller-News',
};

const domainLabel = (d) => DOMAIN_LABELS[d] || d || 'unbekannt';

const formatSize = (bytes) => {
  if (bytes == null) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const DatabasePage = () => {
  const [files, setFiles] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const wasRunning = useRef(false);

  const fetchFiles = useCallback(async () => {
    try {
      const [filesRes, configRes] = await Promise.all([
        axios.get(`${API_BASE}/files`),
        axios.get(`${API_BASE}/config`),
      ]);
      setFiles(Array.isArray(filesRes.data) ? filesRes.data : []);
      setDomains(configRes.data?.domains || []);
      setLoadError('');
    } catch (err) {
      console.error('Error loading database page:', err);
      setLoadError(err.response?.data?.error || 'Fehler beim Laden der Daten.');
    }
    setLoading(false);
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/sync/status`);
      setSyncStatus(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    fetchFiles();
    fetchSyncStatus();
  }, [fetchFiles, fetchSyncStatus]);

  // poll sync status; refresh the file list once a run finishes
  useEffect(() => {
    const t = setInterval(async () => {
      const s = await fetchSyncStatus();
      if (s) {
        if (wasRunning.current && !s.running) fetchFiles();
        wasRunning.current = s.running;
      }
    }, 5000);
    return () => clearInterval(t);
  }, [fetchSyncStatus, fetchFiles]);

  const handleSyncNow = async () => {
    try {
      await axios.post(`${API_BASE}/sync/run`);
      wasRunning.current = true;
      await fetchSyncStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'Synchronisierung konnte nicht gestartet werden.');
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(
      `"${record.displayname || record.filename}" wirklich löschen?\n\nAlle ${record.chunkCount} Chunks werden aus dem Vektor-Index entfernt. Hinweis: Ist die Datei weiterhin in der Quell-API vorhanden, wird sie bei der nächsten Synchronisierung erneut aufgenommen.`
    );
    if (!confirmed) return;
    setDeletingId(record.id);
    try {
      await axios.delete(`${API_BASE}/files/${record.id}`);
      setFiles((prev) => prev.filter((f) => f.id !== record.id));
    } catch (err) {
      console.error('Delete failed:', err);
      alert(err.response?.data?.error || 'Löschen fehlgeschlagen.');
    }
    setDeletingId(null);
  };

  const handleRebuild = async () => {
    const confirmed = window.confirm(
      'Dateiliste aus Pinecone neu aufbauen?\n\nDer gesamte Namespace wird gescannt und die Liste aus den Vektor-Metadaten rekonstruiert.'
    );
    if (!confirmed) return;
    setRebuilding(true);
    try {
      const res = await axios.post(`${API_BASE}/rebuild`);
      await fetchFiles();
      alert(`Liste neu aufgebaut: ${res.data.files} Dateien aus ${res.data.vectors} Vektoren.`);
    } catch (err) {
      console.error('Rebuild failed:', err);
      alert(err.response?.data?.error || 'Neuaufbau fehlgeschlagen.');
    }
    setRebuilding(false);
  };

  // filter chips: configured domains + anything present in the data
  const domainOptions = Array.from(
    new Set([...domains, ...files.map((f) => f.doc_domain).filter(Boolean)])
  );
  const countByDomain = files.reduce((acc, f) => {
    acc[f.doc_domain] = (acc[f.doc_domain] || 0) + 1;
    return acc;
  }, {});

  const visibleFiles = files.filter((f) => {
    if (domainFilter !== 'all' && f.doc_domain !== domainFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (f.filename || '').toLowerCase().includes(q) ||
        (f.displayname || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const running = syncStatus?.running;
  const sourceEntries = Object.entries(syncStatus?.sources || {});
  const totalErrors = sourceEntries.reduce((n, [, s]) => n + (s.errors?.length || 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      {/* Sync status card */}
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Automatische Synchronisierung</h2>
            <p className="mt-1 text-sm text-gray-500">
              Die Wissensdatenbank wird automatisch aus den MDESIGN-Quellen befüllt
              (Module, Software, Hersteller, Website, FAQ, Hersteller-News).
            </p>
          </div>
          <button
            onClick={handleSyncNow}
            disabled={running}
            className="inline-flex items-center rounded-md bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {running ? 'Synchronisierung läuft…' : 'Jetzt synchronisieren'}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
          <span>
            Letzter Lauf:{' '}
            <span className="font-medium text-gray-900">
              {running ? 'läuft gerade…' : formatDate(syncStatus?.finishedAt)}
            </span>
          </span>
          {!running && syncStatus?.nextRunAt && (
            <span>
              Nächster Lauf: <span className="font-medium text-gray-900">{formatDate(syncStatus.nextRunAt)}</span>
            </span>
          )}
          {totalErrors > 0 && (
            <span className="text-red-600">{totalErrors} Fehler im letzten Lauf</span>
          )}
        </div>

        {sourceEntries.length > 0 && (
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {sourceEntries.map(([domain, s]) => (
              <div key={domain} className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                <div className="font-medium text-gray-800">{s.label || domainLabel(domain)}</div>
                {s.running ? (
                  <div className="mt-0.5 text-blue-700">wird synchronisiert…</div>
                ) : (
                  <div className="mt-0.5 text-gray-600">
                    {s.added ?? 0} neu · {s.updated ?? 0} aktualisiert · {s.deleted ?? 0} gelöscht · {s.unchanged ?? 0} unverändert
                    {(s.errors?.length || 0) > 0 && (
                      <span className="text-red-600"> · {s.errors.length} Fehler</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File table */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Dateien im Index{' '}
            {files.length > 0 && (
              <span className="text-sm font-normal text-gray-500">
                ({visibleFiles.length}/{files.length})
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suchen…"
              className="w-44 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={handleRebuild}
              disabled={rebuilding}
              title="Scannt den Pinecone-Namespace und rekonstruiert die Liste aus den Vektor-Metadaten"
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rebuilding ? 'Scanne Pinecone…' : 'Aus Pinecone wiederherstellen'}
            </button>
          </div>
        </div>

        {/* doc_domain filter */}
        <div className="flex flex-wrap gap-2 border-t border-gray-100 px-5 py-3">
          <button
            onClick={() => setDomainFilter('all')}
            className={[
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              domainFilter === 'all'
                ? 'bg-blue-700 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            ].join(' ')}
          >
            Alle ({files.length})
          </button>
          {domainOptions.map((d) => (
            <button
              key={d}
              onClick={() => setDomainFilter(d)}
              className={[
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                domainFilter === d
                  ? 'bg-blue-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
              ].join(' ')}
            >
              {domainLabel(d)} ({countByDomain[d] || 0})
            </button>
          ))}
        </div>

        {loadError && (
          <div className="border-t border-gray-100 px-5 py-4 text-sm text-red-600">{loadError}</div>
        )}

        {loading ? (
          <div className="border-t border-gray-100 px-5 py-8 text-center text-sm text-gray-500">Laden…</div>
        ) : visibleFiles.length === 0 && !loadError ? (
          <div className="border-t border-gray-100 px-5 py-8 text-center text-sm text-gray-500">
            {files.length === 0
              ? 'Noch keine Daten im Index. Die Synchronisierung befüllt die Datenbank automatisch.'
              : 'Keine Dateien für diesen Filter.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="px-5 py-3">Dateiname</th>
                  <th className="px-5 py-3">Anzeigename</th>
                  <th className="px-5 py-3">Domain</th>
                  <th className="px-5 py-3 text-right">Chunks</th>
                  <th className="px-5 py-3 text-right">Größe</th>
                  <th className="px-5 py-3">Aktualisiert</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleFiles.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="max-w-[200px] truncate px-5 py-3 font-medium text-gray-900" title={f.filename}>
                      {f.filename}
                    </td>
                    <td className="max-w-[220px] truncate px-5 py-3 text-gray-700" title={f.displayname || ''}>
                      {f.url ? (
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-700 hover:underline"
                        >
                          {f.displayname || '–'}
                        </a>
                      ) : (
                        f.displayname || '–'
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                        {domainLabel(f.doc_domain)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-700">{f.chunkCount}</td>
                    <td className="px-5 py-3 text-right text-gray-700">{formatSize(f.size)}</td>
                    <td className="px-5 py-3 text-gray-700">{formatDate(f.uploadedAt)}</td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => handleDelete(f)}
                        disabled={deletingId === f.id}
                        className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingId === f.id ? 'Löschen…' : 'Löschen'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DatabasePage;
